import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Label,
  Spinner,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, ThProps, Thead, Tr } from '@patternfly/react-table';
import { ExternalLinkAltIcon, PlayIcon, SearchIcon, StopIcon } from '@patternfly/react-icons';
import { useKeycloak } from '../KeycloakProvider';
import { useTimeTracker } from '../../context/TimeTrackerContext';
import { useTimer } from '../../context/TimerContext';
import { useDashboardFilterOptions, useDashboardFiltersUrl, useIssuesData } from '../../hooks';
import { getStatusLabelColor, isValidJiraIssueKey } from '../../utils';
import { Issue } from '../../types';
import { IssuesToolbar } from './IssuesToolbar';
import { IssueLabelsCell } from '../common';
import { CursorPagination } from '../common';
import { JIRA_BASE_URL } from '../../config';
import './IssuesTable.css';

const EXCLUDED_STATUSES = ['Closed', 'Rejected', 'Done', 'Resolved', 'Close', 'Zamknięte', 'Odrzucone'];
const NO_EXCLUDED_STATUSES: string[] = [];

export const IssuesTable: React.FC = () => {
  const { stopTimer, currentUser, currentUserLoading } = useTimeTracker();
  const { activeTimer, startTimer } = useTimer();
  const { keycloak } = useKeycloak();
  const { initialFilters, updateUrl } = useDashboardFiltersUrl();
  const { availableStatuses: allStatuses, availableLabels } = useDashboardFilterOptions();

  // Get user role from Keycloak
  const userRoles = useMemo(() => {
    const realmRoles = keycloak?.tokenParsed?.realm_access?.roles || [];
    return realmRoles;
  }, [keycloak?.tokenParsed?.realm_access?.roles]);

  const isRegularUser = userRoles.includes('ROLE_USER') && !userRoles.includes('ROLE_ADMIN');

  // Wait for currentUser to load before fetching if user needs assignee filter
  const isReady = !isRegularUser || !currentUserLoading;

  // Pagination state
  const [perPage, setPerPage] = useState(initialFilters.perPage || 20);

  // Filter state - initialized from URL
  const [searchInput, setSearchInput] = useState(initialFilters.search || '');
  const [searchValue, setSearchValue] = useState(initialFilters.search || '');
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setSearchValue(value);
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialFilters.statuses || []);
  const [selectedLabels, setSelectedLabels] = useState<string[]>(initialFilters.labels || []);
  const [isStatusSelectOpen, setIsStatusSelectOpen] = useState(false);
  const [showAllStatuses, setShowAllStatuses] = useState(initialFilters.showAllStatuses || false);

  // Filter out excluded statuses from the dropdown (unless showAllStatuses is enabled)
  const availableStatuses = useMemo(() => {
    if (showAllStatuses) {
      return allStatuses;
    }
    return allStatuses.filter((status) => !EXCLUDED_STATUSES.includes(status));
  }, [allStatuses, showAllStatuses]);

  // Remove excluded statuses from selection when "Include closed" is turned off
  useEffect(() => {
    if (!showAllStatuses && selectedStatuses.length > 0) {
      const filteredStatuses = selectedStatuses.filter(
        (status) => !EXCLUDED_STATUSES.includes(status)
      );
      if (filteredStatuses.length !== selectedStatuses.length) {
        setSelectedStatuses(filteredStatuses);
      }
    }
  }, [showAllStatuses, selectedStatuses]);

  // Sorting state - initialized from URL
  const [activeSortIndex, setActiveSortIndex] = useState<number | null>(initialFilters.sortIndex ?? null);
  const [activeSortDirection, setActiveSortDirection] = useState<'asc' | 'desc'>(initialFilters.sortDirection || 'asc');

  // Update URL when filters change (no page number — keyset pagination)
  useEffect(() => {
    updateUrl({
      search: searchValue,
      statuses: selectedStatuses,
      labels: selectedLabels,
      perPage,
      sortIndex: activeSortIndex,
      sortDirection: activeSortDirection,
      showAllStatuses,
    });
  }, [searchValue, selectedStatuses, selectedLabels, perPage, activeSortIndex, activeSortDirection, showAllStatuses, updateUrl]);

  // Use the issues data hook with cursor-based pagination
  const {
    issues, totalSize, loading, error,
    hasNextPage, hasPreviousPage,
    goToNextPage, goToPreviousPage, goToFirstPage,
    pageStartIndex, retry,
  } = useIssuesData({
    searchValue,
    selectedStatuses,
    selectedLabels,
    excludedStatuses: showAllStatuses ? NO_EXCLUDED_STATUSES : EXCLUDED_STATUSES,
    assigneeId: isRegularUser ? currentUser?.id : undefined,
    perPage,
    activeSortIndex,
    activeSortDirection,
    enabled: isReady,
  });


  const getSortParams = (columnIndex: number): ThProps['sort'] => ({
    sortBy: {
      index: activeSortIndex ?? undefined,
      direction: activeSortDirection,
      defaultDirection: 'asc',
    },
    onSort: (_event, index, direction) => {
      setActiveSortIndex(index);
      setActiveSortDirection(direction);
    },
    columnIndex,
  });

  const handleActionClick = async (issue: Issue) => {
    if (activeTimer?.issueId === issue.id) {
      await stopTimer().catch(() => {});
    } else {
      if (activeTimer) {
        await stopTimer().catch(() => {});
      }
      startTimer(issue);
    }
  };

  const onStatusesChange = (statuses: string[]) => {
    setSelectedStatuses(statuses);
  };

  const onClearAllFilters = () => {
    setSearchInput('');
    setSearchValue('');
    setSelectedStatuses([]);
    setSelectedLabels([]);
  };

  const hasFilters = searchValue || selectedStatuses.length > 0 || selectedLabels.length > 0;

  return (
    <div>
      <IssuesToolbar
        searchValue={searchInput}
        onSearchChange={handleSearchChange}
        selectedStatuses={selectedStatuses}
        availableStatuses={availableStatuses}
        onStatusesChange={onStatusesChange}
        selectedLabels={selectedLabels}
        availableLabels={availableLabels}
        onLabelsChange={setSelectedLabels}
        onClearAllFilters={onClearAllFilters}
        isStatusSelectOpen={isStatusSelectOpen}
        onStatusSelectToggle={setIsStatusSelectOpen}
        showAllStatuses={showAllStatuses}
        onShowAllStatusesChange={setShowAllStatuses}
        pageStartIndex={pageStartIndex}
        itemsOnPage={issues.length}
        totalSize={totalSize}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        onFirstPage={goToFirstPage}
        perPage={perPage}
        onPerPageChange={setPerPage}
        isLoading={loading}
      />

      {loading && issues.length === 0 ? (
        <div className="issues-table-loading">
          <Spinner size="xl" />
        </div>
      ) : error && issues.length === 0 ? (
        <EmptyState titleText="Unable to load issues" icon={SearchIcon} headingLevel="h4">
          <EmptyStateBody>
            Something went wrong while loading your issues. Try refreshing the page.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={retry}>
                Try again
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : issues.length === 0 && !hasFilters ? (
        <EmptyState titleText="No issues yet" icon={SearchIcon} headingLevel="h4">
          <EmptyStateBody>
            You don&apos;t have any issues assigned. Check your Jira board or ask your team lead.
          </EmptyStateBody>
        </EmptyState>
      ) : issues.length === 0 ? (
        <EmptyState titleText="No matching issues" icon={SearchIcon} headingLevel="h4">
          <EmptyStateBody>
            No issues match your filters. Try adjusting your search or status selection.
          </EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="link" onClick={onClearAllFilters}>
                Clear filters
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      ) : (
        <>
          <Table aria-label="Issues table" variant="compact" className="issues-table">
            <Thead>
              <Tr>
                <Th sort={getSortParams(0)} style={{ width: '1%' }} modifier="nowrap">Key</Th>
                <Th sort={getSortParams(1)} modifier="truncate">Summary</Th>
                <Th style={{ width: '1%' }} modifier="nowrap">Labels</Th>
                <Th sort={getSortParams(2)} style={{ width: '1%' }} modifier="nowrap">Status</Th>
                <Th style={{ width: '1%' }} modifier="nowrap">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {issues.map((issue) => {
                const isActive = activeTimer?.issueId === issue.id;
                return (
                  <Tr key={issue.id} className={isActive ? 'issue-row-active' : ''}>
                    <Td dataLabel="Key" modifier="nowrap">
                      <span className="issue-key-wrapper">
                        <strong>{issue.key}</strong>
                        {isValidJiraIssueKey(issue.key) && (
                          <Tooltip content="Open in Jira">
                            <a
                              href={`${JIRA_BASE_URL}/browse/${encodeURIComponent(issue.key)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="issue-jira-link"
                              aria-label={`Open ${issue.key} in Jira`}
                            >
                              <ExternalLinkAltIcon />
                            </a>
                          </Tooltip>
                        )}
                      </span>
                    </Td>
                    <Td dataLabel="Summary" modifier="truncate">{issue.summary}</Td>
                    <Td dataLabel="Labels" modifier="nowrap">
                      <IssueLabelsCell labels={issue.labels} countClassName="issues-labels-count" />
                    </Td>
                    <Td dataLabel="Status" modifier="nowrap">
                      {issue.status ? (
                        <Label color={getStatusLabelColor(issue.status)} isCompact>
                          {issue.status}
                        </Label>
                      ) : (
                        '-'
                      )}
                    </Td>
                    <Td dataLabel="Action" modifier="nowrap">
                      <Tooltip content={isActive ? 'Stop tracking' : 'Start tracking'}>
                        <Button
                          variant={isActive ? 'danger' : 'primary'}
                          icon={isActive ? <StopIcon /> : <PlayIcon />}
                          onClick={() => handleActionClick(issue)}
                          size="sm"
                          className={isActive ? 'issue-action-btn--active' : 'issue-action-btn'}
                          aria-label={isActive ? 'Stop' : 'Start'}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <div className="issues-table-pagination-bottom">
            <CursorPagination
              pageStartIndex={pageStartIndex}
              itemsOnPage={issues.length}
              totalSize={totalSize}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={goToNextPage}
              onPreviousPage={goToPreviousPage}
              onFirstPage={goToFirstPage}
              perPage={perPage}
              onPerPageChange={setPerPage}
              isDisabled={loading}
            />
          </div>
        </>
      )}
    </div>
  );
};
